import { useState, useRef, useCallback, type ReactNode } from "react";

export interface TooltipProps {
  /** The text content to display in the tooltip */
  content: string;
  /** The element that triggers the tooltip on hover/focus */
  children: ReactNode;
}

/**
 * Generic tooltip component that appears on hover/focus within 200ms.
 * Uses CSS transitions for smooth appearance. Designed for use with
 * download buttons to show format descriptions.
 */
export function Tooltip({ content, children }: TooltipProps) {
  const [visible, setVisible] = useState(false);
  const showTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hideTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);

  const show = useCallback(() => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
      hideTimeout.current = null;
    }
    showTimeout.current = setTimeout(() => {
      setVisible(true);
    }, 200);
  }, []);

  const hide = useCallback(() => {
    if (showTimeout.current) {
      clearTimeout(showTimeout.current);
      showTimeout.current = null;
    }
    hideTimeout.current = setTimeout(() => {
      setVisible(false);
    }, 100);
  }, []);

  return (
    <span
      className="tooltip-wrapper"
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
    >
      {children}
      <span
        className={`tooltip ${visible ? "tooltip-visible" : ""}`}
        role="tooltip"
        aria-hidden={!visible}
      >
        {content}
      </span>
    </span>
  );
}
